/**
 * Service contenente metodi per l'utilizzo di SignalR
 * Ref: @microsoft/signalr
 */
import { Injectable } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import _ from 'lodash';
import { environment } from '../../environments/environment';
import { appCommonConstants } from '../app.constants';
import { IAppSignalRAddClientListInput, IAppSignalRGroup, IAppSignalRListener, ISignalRHubMessage } from '../interfaces/signalR.interfaces';
import { AppAuthDataService } from './authData.service';
import { AppEventService } from './event.service';
import { AppLoggerService } from './logger.service';

const NOTIFYEVENTNAME = 'SendClientMessage';
const RECONNECTAFTER_MS = 3000;
const SIGNALR_ENDPOINT = `${environment.appSettings.apiBaseAddress}/${appCommonConstants.signalR.hubName}`;

enum INVOKEMETHODSENUM {
  AddClientInfo = 'addClientInfo',
  AddToGroup = 'AddToGroup',
  AddToGroupList = 'AddToGroupList',
  GroupRemove = 'GroupRemove',
  GroupRemoveList = 'GroupRemoveList'
}

@Injectable({
  providedIn: 'root',
})

export class AppSignalRService {
  constructor(
    private authDataService: AppAuthDataService,
    private eventService: AppEventService,
    private logger: AppLoggerService) {
    // Do null
  }

  public connection?: signalR.HubConnection;
  private groups: IAppSignalRGroup = {};
  private notifySubscription: any;
  private reconnectTimer: any;

  public get isConnected(): boolean {
    return this.connection?.state === signalR.HubConnectionState.Connected;
  }
  public get state(): signalR.HubConnectionState {
    return this.connection?.state || signalR.HubConnectionState.Disconnected;
  }

  /**
   * Sottoscrizione ai gruppi
   * DEPRECATED
   */
  public groupSubscribe(): void {
    if (this.logger.isDebugLevel()) {
      this.logger.console.warn("SignalRService: groupSubscribe is deprecated use groupSubscribeList instead");
    }

    if (!this.connection || !this.isConnected || _.size(this.groups) === 0) {
      return;
    }

    if (this.connection.state == signalR.HubConnectionState.Connected)
      // Aggiunta del client ai gruppi preregistrati
      for (let group in this.groups) {
        let $filter = this.groups[group].filter;

        this.logger.console.debug(`[SignalR] ------> Add client subscription to group: ${group} ${($filter ? " with filter " + JSON.stringify($filter) : " without filter")}`);

        // Invoke a server side method
        this.connection.invoke(INVOKEMETHODSENUM.AddToGroup, group, $filter).then(() => {
          this.logger.console.debug(`[SignalR] ------> Client subscription to group: ${group} OK`);
        }).catch(error => {
          this.logger.console.error(`[SignalR] ------> Error client subscription to group: ${group}`, error);
        });
      }

    // Invoke a server side method
    this.connection.invoke(INVOKEMETHODSENUM.AddClientInfo, this.authDataService.getUsername(), null).then(() => {
      this.logger.console.debug(`[SignalR] ------> Add client info`);
    }).catch(error => {
      this.logger.console.error(`[SignalR] ------> Error add client info`, error);
    });
  }

  /** Sottoscrizione ai gruppi */
  public groupSubscribeList(): void {
    if (!this.connection || !this.isConnected || _.size(this.groups) === 0) {
      return;
    }

    let groups: Array<IAppSignalRAddClientListInput> = _.map(this.groups, x => { return { name: x.name, extraData: x.filter } as IAppSignalRAddClientListInput; });

    this.connection.invoke(INVOKEMETHODSENUM.AddToGroupList, groups, this.authDataService.getUsername(), null).then(() => {
      this.logger.console.debug(`[SignalR] ------> Client subscription to groups: ${_.join(_.map(groups, x => x.name), ", ")} OK`);
    }).catch(error => {
      this.logger.console.error(`[SignalR] ------> Error client subscription to groups: ${_.join(_.map(groups, x => x.name))}`, error);
    });
  }

  /**
   * Rimozione della sottoscrizione ai gruppi specificati
   * @param name
   */
  public groupUnsubscribe(name: string): void {
    if (this.logger.isDebugLevel()) {
      this.logger.console.warn("SignalRService: groupUnsubscribe is deprecated use groupUnsubscribeList instead");
    }

    // Controllo valorizzazione parametri
    if (!name) {
      return;
    }

    // Controllo esistenza connessione
    if (!this.isConnected || this.connection == null || this.connection.connectionId == null) {
      return;
    }

    // Rimozione del client dal gruppo
    this.logger.console.debug(`[SignalR] ------> Remove client subscription to group: ${name}`);

    // Invoke a server side method
    this.connection.invoke(INVOKEMETHODSENUM.GroupRemove, this.connection.connectionId, name).then(() => {
      delete this.groups[name];
      this.logger.console.debug(`[SignalR] ------> Client unsubscription from group: ${name} OK`);
    }).catch(error => {
      this.logger.console.error(`[SignalR] ------> Error client unsubscription from group: ${name}`, error);
    });
  }

  /**
   * Rimozione della sottoscrizione ai gruppi specificati
   * @param names
   */
  public groupUnsubscribeList(names: Array<string>, all = false): void {
    // Controllo valorizzazione parametri
    if (_.size(names) === 0 && !all) {
      return;
    }

    // Controllo esistenza connessione
    if (!this.isConnected || this.connection == null || this.connection.connectionId == null) {
      return;
    }

    let groups: Array<string> = all ? _.map(this.groups, x => x.name) : names;

    /**
     * Invoke a server side method
     * Rimozione del client dai gruppi preregistrati
     */
    this.connection.invoke(INVOKEMETHODSENUM.GroupRemoveList, this.connection.connectionId, groups).then(() => {
      this.logger.console.debug(`[SignalR] ------> Client unsubscription from groups: ${_.join(groups, ", ")} OK`);
      _.forEach(groups, x => delete this.groups[x]);
    }).catch(error => {
      this.logger.console.error(`[SignalR] ------> Error client unsubscription from groups: ${_.join(groups, ", ")}`, error);
    });
  }

  public async initialize(): Promise<void> {
    if (!appCommonConstants.signalR.enabled) {
      return;
    }

    this.eventService.on(appCommonConstants.events.signalR.start).subscribe(
      async () => {
        await this.connect();
      });

    this.eventService.on(appCommonConstants.events.signalR.stop).subscribe(
      async () => {
        await this.disconnect(true);
      });

    this.eventService.on(appCommonConstants.events.logoff).subscribe(
      async () => {
        this.groups = {};
        await this.disconnect(true);
      });

    this.eventService.on(appCommonConstants.events.loggedin).subscribe(
      async () => {
        await this.connect();
      });

    this.eventService.on(appCommonConstants.events.alreadyLoggedin).subscribe(
      async () => {
        await this.connect();
      });

    try {
      this.connection = new signalR.HubConnectionBuilder()
        .withUrl(SIGNALR_ENDPOINT, { accessTokenFactory: () => this.authDataService.getAuthorizationToken() })
        .withAutomaticReconnect()
        .build();
    } catch (error) {
      console.log(`SignalR TEST Sconnection error: ${error}`);
    }
  }

  /**
   * Effettua il set up del singolo listener
   * @param listener
   * @param subscribe
   * @param useList
   */
  public listenerSetUp(listener: IAppSignalRListener, subscribe = true, useList = false): void {
    if (!this.groups[listener.name]) {
      // Autonaming
      if (listener.broadcastEvent == null) {
        listener.broadcastEvent = listener.name;
      }

      this.logger.console.debug(`[SignalR] ------> Register new event with name: ${listener.name} to hub with broadcast event: ${listener.broadcastEvent}`);

      // Gestione broadcast del messaggio utilizzando il broadcastEvent
      let action = (data: any): void => {
        this.logger.console.debug(`[SignalR] ${listener.broadcastEvent}: ${JSON.stringify(data)}`);

        this.eventService.broadcast(listener.broadcastEvent as string, data);

        if (typeof (listener?.callback) === "function") {
          listener.callback(listener.broadcastEvent, data);
        }
      }

      this.addGroupEvent(listener.name, action, true, listener.filter);
    }

    if (!listener.filter) {
      listener.filter = false;
    }

    let changed = !_.isEqual(this.groups[listener.name].filter, listener.filter);

    this.groups[listener.name].filter = listener.filter;
    this.groups[listener.name].changed = changed;

    if (subscribe) {
      if (useList) {
        this.groupSubscribeList();
      } else {
        this.groupSubscribe();
      }
    }
  }

  /**
   * Effettua il set up di un array di listener
   * @param listenerList
   * @param subscribe
   * @param useList
   */
  public listenerSetUpList(listenerList: IAppSignalRListener[], subscribe = true, useList = false): void {
    if (!_.size(listenerList)) {
      return;
    }

    listenerList.forEach((item) => {
      this.listenerSetUp(item, false);
    });

    if (subscribe) {
      if (useList) {
        this.groupSubscribeList();
      } else {
        this.groupSubscribe();
      }
    }
  }

  /**
   * Aggiunge il gruppo all'array degli eventi
   * @param name
   * @param callback
   * @param dependency
   * @param startFilter
   */
  private addGroupEvent(name: string, callback: (msg: ISignalRHubMessage) => void, dependency: any, startFilter: any): void {
    if (name == null || callback == null) {
      throw ("name or callback null")
    }

    // In questo modo effettua l'eventuale override di un gruppo già specificato
    this.groups[name] = {
      name: name,
      callback: callback,
      dependency: dependency || false,
      filter: startFilter || false,
      changed: true
    };

    this.logger.console.debug(`[SignalR] ------> Add new groups event with name: ${name} to hub`);
  }

  private async connect() {
    if (this.isConnected || this.connection?.state == signalR.HubConnectionState.Connecting || this.connection?.state == signalR.HubConnectionState.Reconnecting) {
      return;
    }

    if (!this.connection || !appCommonConstants.signalR.enabled) {
      return;
    }

    await this.connection.start()
      .then(() => {
        clearInterval(this.reconnectTimer);

        this.eventService.broadcast(appCommonConstants.events.signalR.initialized);
        this.logger.console.debug(`[SignalR] ------>\n connection success! Connection id: ${this.connection?.connectionId}`);

        // Registrazione evento ricezione messaggi
        this.connection?.on(NOTIFYEVENTNAME, (msg: ISignalRHubMessage) => {
          if (environment.appSettings.logger) {
            this.logger.console.debug(`[SignalR] ------>\n ${NOTIFYEVENTNAME} message: ${JSON.stringify(msg)}`);
          }
          _.forEach(_.filter(this.groups, g => g.name === msg.groupName), x => {
            x?.callback(msg);
          });
        });

        // Disconnessione client
        window.addEventListener("beforeunload", async (e) => {
          await this.connection?.stop();
        });
      })
      .catch(() => {
        this.logger.console.error(`[SignalR] error during connection, automatic reconnect after ${RECONNECTAFTER_MS}ms`);
        if (appCommonConstants.signalR.reconnectIntervalMs) {
          setTimeout(() => {
            this.reconnectTimer = setInterval(() => this.connect(), appCommonConstants.signalR.reconnectIntervalMs);
          }, RECONNECTAFTER_MS);
        }
      });

    // Evento scatenato alla riconnessione
    this.connection.onreconnected(() => {
      this.groupSubscribeList();
    });
  }

  /**
   * Effettua la disconnessione
   * @param reset
   */
  private async disconnect(reset: boolean) {
    if (reset) {
      // Reset dei filtri
      this.logger.console.debug(`[SignalR] ------> Group filter reset`);
      for (let group in this.groups) {
        this.groups[group].filter = false;
      }

      if (this.notifySubscription) {
        this.notifySubscription.unsubscribe();
      }
    }

    if (this.isConnected) {
      if (this.connection) {
        await this.connection.stop();
      }
    }
  }
}
